import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  CalendarClock,
  CalendarDays,
  CalendarSync,
  CalendarOff,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  Info,
  Plus,
} from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Menu, type MenuItem } from '@/components/ui/Menu';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { StatCard } from '@/components/ui/StatCard';
import type { Option } from '@/constants/institute';
import { toast } from '@/hooks/useToast';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { EventDetailsModal } from './EventDetailsModal';
import { EventRoster } from './EventRoster';
import { MonthGrid } from './MonthGrid';
import { PANEL, RailPanel } from './RailPanel';
import { UpcomingEvents } from './UpcomingEvents';
import { WeekGrid } from './WeekGrid';
import { YearGrid } from './YearGrid';
import {
  academicYearLabel,
  AUDIENCE_OPTIONS,
  CATEGORY,
  CATEGORY_OPTIONS,
  CATEGORY_ORDER,
  CLASS_SCOPE_OPTIONS,
  HEADLINE_CATEGORIES,
  MONTHS,
} from '../constants';
import {
  addDays,
  addMonths,
  clampDay,
  monthsBetween,
  overlapsRange,
  startOfMonth,
  startOfWeek,
  todayISO,
  weekCells,
} from '../utils/dates';
import { buildCalendarFile, downloadCalendarFile } from '../utils/ics';
import type {
  CalendarAudience,
  CalendarClassScope,
  CalendarEventCategory,
  CalendarEventItem,
  CalendarItemCategory,
  CalendarSession,
  CalendarViewMode,
} from '../types/calendar';

/** How many entries the rail lists before "view all" is the better answer. */
const UPCOMING_LIMIT = 5;

const VIEWS: { value: CalendarViewMode; label: string }[] = [
  { value: 'MONTH', label: 'Month' },
  { value: 'WEEK', label: 'Week' },
  { value: 'YEAR', label: 'Year' },
  { value: 'LIST', label: 'List' },
];

/** `ANY` is the filter's own value — an event is never addressed to "anyone". */
const ANY = 'ANY';

const AUDIENCE_FILTER_OPTIONS: Option<CalendarAudience | typeof ANY>[] = [
  { value: ANY, label: 'Anyone' },
  ...AUDIENCE_OPTIONS,
];

const SCOPE_FILTER_OPTIONS: Option<CalendarClassScope | typeof ANY>[] = [
  { value: ANY, label: 'Any class' },
  ...CLASS_SCOPE_OPTIONS,
];

/**
 * What the office can do to the calendar that a reader cannot. The view draws
 * the buttons; the page that supplies these owns the form, the confirmation
 * and the API calls.
 */
export interface CalendarManagement {
  onAdd: (defaults: { date?: string; category?: CalendarEventCategory }) => void;
  onEdit: (item: CalendarEventItem) => void;
  onRemove: (item: CalendarEventItem) => void;
  /**
   * Declaring a holiday. Optional, because holidays carry their own key: the
   * page passes it only to someone who may declare one, and the same switch
   * decides whether a holiday on the grid can be edited or withdrawn.
   */
  onAddHoliday?: () => void;
}

export interface AcademicCalendarViewProps {
  /**
   * The whole session in one load.
   *
   * Every view — month, week, year and list — reads the same array, so paging
   * through the year costs nothing and never shows a half-drawn grid. The
   * server decides which session that is, and — for a family — which entries
   * were addressed to them; it is never asked for by the client.
   */
  data: CalendarSession | null;
  isLoading: boolean;
  error: { message: string } | null;
  onRetry: () => void;
  title: string;
  description: string;
  /** What an empty session says — who fills it in differs by reader. */
  emptyDescription: string;
  /** The console's palette, put on the root. */
  className?: string;
  /**
   * Adding, editing and removing entries. The office's screen supplies it;
   * without it the calendar is read-only — no add buttons, no edit or remove,
   * and no audience or class filters, since the server has already narrowed
   * the list to this reader.
   */
  manage?: CalendarManagement;
  /**
   * The day to open on, `YYYY-MM-DD`. Today when absent — only a link that
   * means a particular month, such as the roster's working-days tile, sets it.
   */
  initialDate?: string;
  /**
   * Drawn between the header and the calendar — the office's working-days
   * panel. Readers' planners pass nothing and look exactly as before.
   */
  summary?: ReactNode;
  /**
   * Leaves out the row of one-card-per-kind counts. The office's planner does:
   * its working-days panel is its summary, and the filter chips below already
   * carry the same count for each kind.
   */
  hideCategoryTiles?: boolean;
}

/**
 * The academic calendar — month, week, year and list views of one session, with
 * the upcoming entries and a calendar file to take away.
 *
 * Shared by the office's planner (`AcademicCalendarPage`, which manages it) and
 * the parent panel (which reads it), so both are drawn by the same hand and a
 * date cannot look one way to the office and another to a family.
 */
export function AcademicCalendarView({
  data,
  isLoading,
  error,
  onRetry,
  title,
  description,
  emptyDescription,
  className,
  manage,
  initialDate,
  summary,
  hideCategoryTiles = false,
}: AcademicCalendarViewProps) {
  const events = useMemo(() => data?.items ?? [], [data]);

  /** The day the screen is looking at. Every view is derived from this one. */
  const [cursor, setCursor] = useState<string>(() => initialDate ?? todayISO());
  const [view, setView] = useState<CalendarViewMode>('MONTH');

  const [category, setCategory] = useState<CalendarItemCategory | 'ALL'>('ALL');
  const [audience, setAudience] = useState<CalendarAudience | typeof ANY>(ANY);
  const [classScope, setClassScope] = useState<CalendarClassScope | typeof ANY>(ANY);

  const [viewingEvent, setViewingEvent] = useState<CalendarEventItem | null>(null);

  /**
   * The session's real ends, from the database rather than assumed to be April
   * to March — this institute's year opens on 31 March, and the next one's may
   * open anywhere. Before the load lands, today stands in so the grid has a
   * month to draw rather than flashing an empty one.
   */
  const session = useMemo(
    () => ({
      from: data?.startDate ?? todayISO(),
      to: data?.endDate ?? todayISO(),
      name: data?.academicYear.name ?? '',
    }),
    [data],
  );

  /**
   * The cursor follows the session once it arrives.
   *
   * Opening on today and finding today outside the session — an office looking
   * at the calendar in the fortnight before the year opens — would otherwise
   * draw an empty month while the entries sat one step away.
   */
  useEffect(() => {
    if (!data) return;
    setCursor((current) => clampDay(current, data.startDate, data.endDate));
  }, [data]);

  /** What the filter bar has left showing, in every view. */
  const visibleEvents = useMemo(
    () =>
      events.filter(
        (item) =>
          (category === 'ALL' || item.category === category) &&
          (audience === ANY || item.audience === audience) &&
          (classScope === ANY || item.classScope === classScope),
      ),
    [events, category, audience, classScope],
  );

  /**
   * The same list, still in the session.
   *
   * The server already returns one session, sorted — the overlap test is kept
   * because a multi-day entry may start before the session opens and still
   * belong on its grid.
   */
  const rosterEvents = useMemo(
    () => visibleEvents.filter((item) => overlapsRange(item, session.from, session.to)),
    [visibleEvents, session],
  );

  /** How many of each kind the session holds — the tiles and the filter chips. */
  const counts = useMemo(() => {
    const tally = {} as Record<CalendarItemCategory, number>;
    for (const key of CATEGORY_ORDER) tally[key] = 0;
    for (const item of events) tally[item.category] += 1;
    return tally;
  }, [events]);

  /**
   * The next few things due, soonest first, matching any active category/audience filters.
   *
   * Measured from the real today rather than the period being browsed: an
   * administrator paging back to last year still wants to know what is coming
   * this week.
   */
  const upcoming = useMemo(() => {
    const today = todayISO();
    return visibleEvents
      .filter((item) => item.endDate >= today)
      .sort((a, b) => a.startDate.localeCompare(b.startDate))
      .slice(0, UPCOMING_LIMIT);
  }, [visibleEvents]);

  const isSessionView = view === 'YEAR' || view === 'LIST';

  /** What the reader is looking at, written the way they would say it. */
  const periodLabel = useMemo(() => {
    if (isSessionView) return academicYearLabel(session.name);
    if (view === 'MONTH') {
      return `${MONTHS[Number(cursor.slice(5, 7)) - 1]} ${cursor.slice(0, 4)}`;
    }
    const week = weekCells(cursor);
    return `${formatDate(week[0])} – ${formatDate(week[6])}`;
  }, [cursor, isSessionView, session.name, view]);

  /**
   * The months of this session.
   *
   * One session, because that is what the server serves: the academic year is
   * its decision, so a menu offering the years either side would page onto
   * grids that could only ever be empty.
   */
  const periodItems: MenuItem[] = useMemo(
    () =>
      monthsBetween(session.from, session.to).map(({ year, month }) => ({
        key: `${year}-${month}`,
        label: `${MONTHS[month]} ${year}`,
        onSelect: () =>
          setCursor(
            clampDay(
              `${year}-${String(month + 1).padStart(2, '0')}-01`,
              session.from,
              session.to,
            ),
          ),
      })),
    [session],
  );

  /**
   * One step back or forward, in whatever unit the current view reads in, and
   * never off the session — there is nothing loaded on either side of it.
   */
  function step(direction: 1 | -1) {
    const next =
      view === 'MONTH'
        ? addMonths(startOfMonth(cursor), direction)
        : view === 'WEEK'
          ? addDays(startOfWeek(cursor), 7 * direction)
          : addMonths(cursor, 12 * direction);

    setCursor(clampDay(next, session.from, session.to));
  }

  /** Whether a step in that direction would land anywhere new. */
  const canStep = (direction: 1 | -1) =>
    direction === -1 ? cursor > session.from : cursor < session.to;

  /** Closes the details first, so the form does not open underneath them. */
  function edit(item: CalendarEventItem) {
    setViewingEvent(null);
    manage?.onEdit(item);
  }

  function remove(item: CalendarEventItem) {
    setViewingEvent(null);
    manage?.onRemove(item);
  }

  /** From the year grid or a full day cell: show that day in the month view. */
  function openDay(iso: string) {
    setCursor(clampDay(iso, session.from, session.to));
    setView(view === 'YEAR' ? 'MONTH' : 'WEEK');
  }

  function handleSync() {
    const label = academicYearLabel(session.name);
    downloadCalendarFile(
      buildCalendarFile(events, label),
      `academic-calendar-${session.name}.ics`,
    );
    toast.success(
      'Your calendar file is ready',
      'Import it into Google, Outlook or Apple Calendar to see every entry of this session.',
    );
  }

  /**
   * What the add menu offers.
   *
   * `CATEGORY_OPTIONS`, not `CATEGORY_ORDER`: holidays and vacations are rows
   * in `holidays` — the table the attendance register reads — so "New holiday"
   * opens the holiday form rather than the event form.
   */
  const onAddHoliday = manage?.onAddHoliday;
  const addMenuItems: MenuItem[] = [
    ...CATEGORY_OPTIONS.map(({ value, label }) => ({
      key: value,
      label,
      icon: <span className={cn('h-2 w-2 rounded-full', CATEGORY[value].dot)} aria-hidden />,
      onSelect: () => manage?.onAdd({ date: cursor, category: value }),
    })),
    ...(onAddHoliday
      ? [
          {
            key: 'HOLIDAY',
            label: 'Holiday',
            icon: <span className={cn('h-2 w-2 rounded-full', CATEGORY.HOLIDAY.dot)} aria-hidden />,
            onSelect: onAddHoliday,
          },
        ]
      : []),
  ];

  return (
    <div className={cn('flex flex-col gap-6 animate-hero-zoom', className)}>
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 ">
            <h1 className="text-2xl font-black tracking-tight text-ink">{title}</h1>
            {session.name && (
              <span className="inline-flex mt-1.5 items-center rounded-full border border-indigo-200/70 dark:border-indigo-800/60 bg-indigo-50 dark:bg-indigo-950/40 px-2.5 py-0.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                {academicYearLabel(session.name)}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm font-medium text-ink-2">{description}</p>
        </div>

        {manage && (
          <div className="flex shrink-0 flex-wrap items-center gap-3">
            {onAddHoliday && (
              <Button variant="secondary" leftIcon={<CalendarOff className="h-4 w-4" />} onClick={onAddHoliday}>
                Declare holiday
              </Button>
            )}
            {/* A split button: the face adds an entry, the arrow says what kind. */}
            <div className="flex items-stretch">
              <button
                type="button"
                className="rounded-l-xl rounded-r-none bg-indigo-600 tracking-wide hover:bg-indigo-700 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-2 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                onClick={() => manage.onAdd({ date: cursor })}
              >
                <Plus className="h-4 w-4 stroke-[2.5]" />
                <span>Add event</span>
              </button>
              <Menu
                label="Add an entry of a particular kind"
                items={addMenuItems}
                trigger={<ChevronDown className="h-4 w-4" aria-hidden />}
                triggerClassName="h-[38px] w-9 rounded-r-xl rounded-l-none border-y border-r border-l-0 border-indigo-500 bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center justify-center cursor-pointer transition-all shadow-sm shadow-indigo-500/20 active:scale-[0.98]"
              />
            </div>
          </div>
        )}
      </header>

      {summary}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="flex flex-col gap-6 xl:col-span-9">
          {/* One figure per kind the Add event menu offers (non-clickable stat cards). */}
          {!hideCategoryTiles && (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 animate-slide-down delay-50">
              {HEADLINE_CATEGORIES.map((key) => {
                const style = CATEGORY[key];
                const Icon = style.icon;

                return (
                  <StatCard
                    key={key}
                    label={style.short}
                    value={counts[key]}
                    // hint={TILE_HINT}
                    variant={style.variant}
                    icon={<Icon className="h-5 w-5 text-white" />}
                  />
                );
              })}
            </div>
          )}

          <section className={cn(PANEL, 'flex flex-col gap-3.5 p-3.5 sm:p-4 animate-slide-down delay-100')}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              {view !== 'LIST' ? (
                <Menu
                  label="Jump to another period"
                  align="start"
                  items={periodItems}
                  trigger={
                    <span className="inline-flex items-center gap-2">
                      <span className="text-base font-black tracking-tight text-ink">
                        {periodLabel}
                      </span>
                      <ChevronDown className="h-4 w-4 text-ink-3" aria-hidden />
                    </span>
                  }
                  triggerClassName="cursor-pointer rounded-xl px-2 py-1 hover:bg-surface-2"
                />
              ) : (
                <div />
              )}

              <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                {/* Audience and Scope selects inline next to view tabs. The office
                    only: a reader's list was narrowed to them by the server. */}
                {manage && (
                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    <div className="w-[calc(50%-0.25rem)] sm:w-40">
                      <Select
                        label="Who it is for"
                        hideLabel
                        minPanelWidth={200}
                        className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors w-full"
                        options={AUDIENCE_FILTER_OPTIONS}
                        value={audience}
                        onChange={(e) => setAudience(e.target.value as CalendarAudience | typeof ANY)}
                      />
                    </div>
                    <div className="w-[calc(50%-0.25rem)] sm:w-40">
                      <Select
                        label="Which classes"
                        hideLabel
                        minPanelWidth={200}
                        className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors w-full"
                        options={SCOPE_FILTER_OPTIONS}
                        value={classScope}
                        onChange={(e) =>
                          setClassScope(e.target.value as CalendarClassScope | typeof ANY)
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Four ways of reading the same session */}
                <div className="flex items-center gap-1 rounded-full border border-line bg-surface-2 p-1 overflow-x-auto max-w-full">
                  {VIEWS.map((option) => {
                    const isOn = view === option.value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={isOn}
                        onClick={() => setView(option.value)}
                        className={cn(
                          'cursor-pointer rounded-full px-3 sm:px-3.5 py-1.5 text-[11px] sm:text-[12px] font-bold transition-all whitespace-nowrap',
                          isOn ? 'bg-accent text-white shadow-sm' : 'text-ink-3 hover:text-ink',
                        )}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {isLoading && (
              <div className="flex flex-col gap-3" aria-busy="true">
                <span className="sr-only">Loading the academic calendar</span>
                <div className="grid grid-cols-7 gap-2">
                  {Array.from({ length: 7 }, (_, index) => (
                    <Skeleton key={index} className="h-5 rounded-lg" />
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {Array.from({ length: 35 }, (_, index) => (
                    <Skeleton key={index} className="h-16 rounded-xl" />
                  ))}
                </div>
              </div>
            )}

            {!isLoading && error && (
              <QueryError
                title="Could not load the academic calendar"
                error={error}
                onRetry={onRetry}
              />
            )}

            {!isLoading && !error && events.length === 0 && (
              <EmptyState
                icon={<CalendarDays className="h-5 w-5" />}
                title="Nothing on the calendar yet"
                description={emptyDescription}
                action={
                  manage && (
                    <Button
                      leftIcon={<Plus className="h-4 w-4" />}
                      onClick={() => manage.onAdd({ date: cursor })}
                    >
                      Add the first entry
                    </Button>
                  )
                }
              />
            )}

            {!isLoading && !error && events.length > 0 && view === 'MONTH' && (
              <MonthGrid
                cursor={cursor}
                events={visibleEvents}
                onSelectEvent={setViewingEvent}
                onAddOn={manage && ((iso) => manage.onAdd({ date: iso }))}
                onOpenDay={openDay}
              />
            )}

            {!isLoading && !error && events.length > 0 && view === 'WEEK' && (
              <WeekGrid
                cursor={cursor}
                events={visibleEvents}
                onSelectEvent={setViewingEvent}
                onAddOn={manage && ((iso) => manage.onAdd({ date: iso }))}
              />
            )}

            {!isLoading && !error && events.length > 0 && view === 'YEAR' && (
              <YearGrid
                from={session.from}
                to={session.to}
                events={rosterEvents}
                onOpenDay={openDay}
              />
            )}

            {!isLoading && !error && events.length > 0 && view === 'LIST' && (
              <div className="flex flex-col gap-4">
                <h2 className="text-sm font-black tracking-tight text-ink">
                  {rosterEvents.length} {rosterEvents.length === 1 ? 'entry' : 'entries'}
                  {category !== 'ALL' && ` · ${CATEGORY[category].label}`}
                </h2>

                {rosterEvents.length === 0 ? (
                  <p className="rounded-2xl border border-dashed border-line bg-surface-2 p-8 text-center text-xs font-medium text-ink-3">
                    Nothing on the calendar for that filter.
                  </p>
                ) : (
                  <EventRoster
                    events={rosterEvents}
                    onSelect={setViewingEvent}
                    onEdit={manage?.onEdit}
                    onRemove={manage?.onRemove}
                  />
                )}
              </div>
            )}

            {!isLoading && !error && events.length > 0 && view !== 'LIST' && view !== 'YEAR' && (
              <p className="flex items-start gap-2 rounded-2xl border border-line bg-surface-2 p-3 text-[12px] font-medium text-ink-2">
                <Info className="mt-px h-4 w-4 shrink-0 text-accent" aria-hidden />
                <span>
                  <span className="font-black text-ink">Note:</span> Events and dates are
                  subject to change. Please check regularly for the latest updates.
                </span>
              </p>
            )}
          </section>
        </div>

        {/* The rail */}
        <aside className="flex flex-col gap-6 xl:col-span-3 animate-slide-down delay-180">
          {/* Top Date Navigation Bar */}
          <div className="flex items-center justify-between gap-2 p-2.5 rounded-2xl bg-slate-50 dark:bg-[#0b1322] border border-slate-200/90 dark:border-slate-800 shadow-xs">
            <Menu
              label="Jump to another period"
              align="start"
              items={periodItems}
              trigger={<CalendarDays className="h-4 w-4 text-indigo-600 dark:text-indigo-400" aria-hidden />}
              triggerClassName="h-9 w-9 border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-200 hover:border-indigo-300 transition-colors shadow-2xs shrink-0"
            />

            <div className="flex items-center gap-1.5 min-w-0">
              <button
                type="button"
                aria-label="Previous period"
                onClick={() => step(-1)}
                disabled={!canStep(-1)}
                className="grid h-8 w-8 cursor-pointer place-items-center rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-200 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors shadow-2xs shrink-0 disabled:cursor-not-allowed disabled:opacity-45"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              <span className="truncate px-1.5 text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100 min-w-[85px] text-center">
                {periodLabel}
              </span>

              <button
                type="button"
                aria-label="Next period"
                onClick={() => step(1)}
                disabled={!canStep(1)}
                className="grid h-8 w-8 cursor-pointer place-items-center rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-200 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors shadow-2xs shrink-0 disabled:cursor-not-allowed disabled:opacity-45"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <Button
              size="sm"
              variant={cursor === todayISO() && view === 'MONTH' ? 'primary' : 'secondary'}
              onClick={() => {
                setCursor(clampDay(todayISO(), session.from, session.to));
                setView('MONTH');
                toast.info('Jumped to Today', 'Showing the current month calendar view.');
              }}
              className={cn(
                'font-bold shrink-0 transition-all',
                cursor === todayISO() && view === 'MONTH' && 'bg-indigo-600 text-white shadow-xs ring-2 ring-indigo-500/30 hover:bg-indigo-700'
              )}
            >
              Today
            </Button>
          </div>

          {/* Filter Category Panel (Open by Default) */}
          <div className="flex flex-col gap-2 rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50 dark:bg-[#0b1322] p-2.5 shadow-xs">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                <Filter className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" aria-hidden />
                Filter Category
              </span>
              {category !== 'ALL' && (
                <button
                  type="button"
                  onClick={() => setCategory('ALL')}
                  className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                >
                  Reset category
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button
                type="button"
                aria-pressed={category === 'ALL'}
                onClick={() => setCategory('ALL')}
                className={cn(
                  'cursor-pointer rounded-full border px-3 py-1.5 text-xs font-bold transition-all inline-flex items-center gap-1.5',
                  category === 'ALL'
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-300'
                    : 'border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:border-indigo-300',
                )}
              >
                Everything
              </button>

              {CATEGORY_ORDER.map((key) => {
                const style = CATEGORY[key];
                const isOn = category === key;

                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={isOn}
                    onClick={() => setCategory(isOn ? 'ALL' : key)}
                    className={cn(
                      'inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold transition-all',
                      isOn
                        ? style.chip
                        : 'border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:border-indigo-300',
                    )}
                  >
                    <span className={cn('h-2 w-2 rounded-full', style.dot)} aria-hidden />
                    {style.short}
                    <span
                      className={cn(
                        'ml-0.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-black tabular-nums border shadow-2xs transition-colors',
                        isOn
                          ? 'border-indigo-300 dark:border-indigo-700 bg-white/90 dark:bg-indigo-900/80 text-indigo-700 dark:text-indigo-200'
                          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200',
                      )}
                    >
                      {counts[key]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <RailPanel
            icon={CalendarClock}
            title="Upcoming events"
            action={
              upcoming.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setView('LIST')}
                  className="shrink-0 cursor-pointer text-[12px] font-black text-accent hover:underline"
                >
                  View all
                </button>
              ) : undefined
            }
          >
            {upcoming.length === 0 ? (
              <p className="text-xs font-medium text-ink-3">
                {category !== 'ALL' || audience !== ANY || classScope !== ANY
                  ? 'No upcoming events match the active filters.'
                  : 'Nothing ahead. Everything on the calendar has already passed.'}
              </p>
            ) : (
              <UpcomingEvents events={upcoming} onSelect={setViewingEvent} />
            )}
          </RailPanel>

          <RailPanel icon={CalendarSync} title="Download calendar">
            <p className="text-xs font-medium text-ink-2">
              Take {academicYearLabel(session.name).toLowerCase()} with you — the file imports
              into Google, Outlook or Apple Calendar.
            </p>
            <Button
              variant="secondary"
              fullWidth
              size="md"
              onClick={handleSync}
              disabled={events.length === 0}
              leftIcon={<CalendarSync className="h-4 w-4" />}
            >
              Download calendar (.ics)
            </Button>
          </RailPanel>
        </aside>
      </div>

      <EventDetailsModal
        eventItem={viewingEvent}
        isOpen={Boolean(viewingEvent)}
        onClose={() => setViewingEvent(null)}
        onEdit={manage && edit}
        onRemove={manage && remove}
        canManageHolidays={Boolean(onAddHoliday)}
      />
    </div>
  );
}
